/**
 * @file useOxPay.js
 * @description OxPay React Hook - 컴포넌트에서 OxPay 기능을 사용하기 위한 커스텀 훅
 *
 * 이 훅은 React 컴포넌트에서 OxPay 기능을 쉽게 사용할 수 있도록
 * 상태 관리와 API 호출을 추상화합니다.
 *
 * 주요 기능:
 * - OxPay 계정 상태 자동 추적 (hasAccount, userId, kycStatus)
 * - 모든 API 메서드에 대한 일관된 에러 처리
 * - 메모리 누수 방지 (cleanup, abort controller)
 * - 자동 상태 업데이트 및 동기화
 *
 * 사용 예시:
 * const { hasAccount, createAccount, getCards, startKYC } = useOxPay();
 *
 * 반환 값:
 * - 상태: hasAccount, userId, kycStatus, loading
 * - 계정 관리: createAccount, refreshStatus
 * - KYC: startKYC
 * - 카드: getCards, createCard, activateCard, lockCard, unlockCard, setPin
 * - 지갑: getWallet, getTransactions, topUp, transfer
 *
 * @module hooks/useOxPay
 */

import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { oxpayAPI } from "@/lib/oxpay";
import {
  hasOxPayAccount,
  getOxPayUserId,
  createOxPayAccount,
  syncKYCStatus,
} from "@/lib/oxpay-supabase";

export function useOxPay() {
  const { user } = useAuth();
  const [oxpayStatus, setOxpayStatus] = useState({
    hasAccount: false,
    userId: null,
    kycStatus: null,
    loading: true,
  });

  // Track if component is mounted to prevent memory leaks
  const isMounted = useRef(true);

  // Check and sync OxPay status
  useEffect(() => {
    // Abort controller for cleanup
    const abortController = new AbortController();

    const checkStatus = async () => {
      // No user = clear status
      if (!user) {
        setOxpayStatus({
          hasAccount: false,
          userId: null,
          kycStatus: null,
          loading: false,
        });
        return;
      }

      try {
        setOxpayStatus((prev) => ({ ...prev, loading: true }));

        const hasAccount = hasOxPayAccount(user);
        const userId = getOxPayUserId(user);

        // Sync KYC status if user has account
        let kycStatus = null;
        if (userId && !abortController.signal.aborted) {
          // Don't let KYC sync failure block the entire flow
          try {
            kycStatus = await syncKYCStatus(user);
          } catch (kycError) {
            console.warn("Could not sync KYC status:", kycError.message);
            // Continue without KYC status
          }
        }

        // Only update if component is still mounted and not aborted
        if (isMounted.current && !abortController.signal.aborted) {
          setOxpayStatus({
            hasAccount,
            userId,
            kycStatus,
            loading: false,
          });
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error("Error checking OxPay status:", error);
          if (isMounted.current) {
            // Set loading to false even on error
            setOxpayStatus({
              hasAccount: false,
              userId: null,
              kycStatus: null,
              loading: false,
            });
          }
        }
      }
    };

    checkStatus();

    // Cleanup function
    return () => {
      abortController.abort();
    };
  }, [user?.id]); // Re-run when user ID changes

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Refresh status manually
  const refreshStatus = async () => {
    if (!user) return;

    try {
      setOxpayStatus((prev) => ({ ...prev, loading: true }));

      const hasAccount = hasOxPayAccount(user);
      const userId = getOxPayUserId(user);

      let kycStatus = null;
      if (userId) {
        kycStatus = await syncKYCStatus(user);
      }

      if (isMounted.current) {
        setOxpayStatus({
          hasAccount,
          userId,
          kycStatus,
          loading: false,
        });
      }
    } catch (error) {
      console.error("Error refreshing status:", error);
      if (isMounted.current) {
        setOxpayStatus((prev) => ({ ...prev, loading: false }));
      }
    }
  };

  // Create OxPay account (delegates to oxpay-supabase)
  const createAccount = async (additionalData = {}) => {
    if (!user) {
      return {
        success: false,
        error: "User not authenticated",
      };
    }

    try {
      const result = await createOxPayAccount(user, additionalData);

      if (result.success && isMounted.current) {
        // Update status immediately without re-fetching
        setOxpayStatus((prev) => ({
          ...prev,
          hasAccount: true,
          userId: result.oxpayUserId,
        }));

        // Then sync KYC status
        if (result.oxpayUserId) {
          const kycStatus = await syncKYCStatus(user);
          if (isMounted.current) {
            setOxpayStatus((prev) => ({ ...prev, kycStatus }));
          }
        }
      }

      return result;
    } catch (error) {
      console.error("Error creating account:", error);
      return {
        success: false,
        error: error.message || "Failed to create account",
      };
    }
  };

  // All other operations just pass through to the API with consistent error handling
  const apiCall = async (apiMethod, ...args) => {
    try {
      const result = await apiMethod(...args);
      return {
        success: true,
        data: result,
      };
    } catch (error) {
      console.error(`API Error:`, error);
      return {
        success: false,
        error: error.message || "Operation failed",
        data: null,
      };
    }
  };

  return {
    // Status
    ...oxpayStatus,
    isLoading: oxpayStatus.loading,

    // Account operations
    createAccount,
    refreshStatus,

    // Direct API operations (with userId from status)
    startKYC: () => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account found",
        });
      }
      return apiCall(oxpayAPI.startKYC, oxpayStatus.userId);
    },

    getCards: () => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account",
          data: [],
        });
      }
      return apiCall(oxpayAPI.getCards, oxpayStatus.userId);
    },

    createCard: (cardData) => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account",
        });
      }
      return apiCall(oxpayAPI.createCard, oxpayStatus.userId, cardData);
    },

    getWallet: () => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account",
          data: null,
        });
      }
      return apiCall(oxpayAPI.getWallet, oxpayStatus.userId);
    },

    getTransactions: (filters = {}) => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account",
          data: [],
        });
      }
      return apiCall(oxpayAPI.getTransactions, oxpayStatus.userId, filters);
    },

    // Direct pass-through for card operations
    activateCard: (cardId, lastFour) =>
      apiCall(oxpayAPI.activateCard, cardId, lastFour),
    lockCard: (cardId) => apiCall(oxpayAPI.lockCard, cardId),
    unlockCard: (cardId) => apiCall(oxpayAPI.unlockCard, cardId),
    setPin: (cardId, pin) => apiCall(oxpayAPI.setPin, cardId, pin),

    // Direct pass-through for wallet operations
    topUp: (amount, method) => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account",
        });
      }
      return apiCall(oxpayAPI.topUp, oxpayStatus.userId, amount, method);
    },

    transfer: (toUserId, amount, description) => {
      if (!oxpayStatus.userId) {
        return Promise.resolve({
          success: false,
          error: "No user account",
        });
      }
      return apiCall(
        oxpayAPI.transfer,
        oxpayStatus.userId,
        toUserId,
        amount,
        description
      );
    },
  };
}
